/**
 * PoseTracker — full-body 33-landmark tracking via MediaPipe Pose Landmarker.
 *
 * Lazy-loads `@mediapipe/tasks-vision` from CDN on first `load()` call so
 * games that don't need pose tracking pay zero bundle cost.
 *
 * Coordinates are normalized to [0, 1] in image space (x = right, y = down).
 * `worldLandmarks` (when available) are in real-world meters relative to
 * the hip center, useful for measuring real distances and angles.
 *
 * Example: detect a jumping jack by watching wrist height vs shoulder height.
 *
 *   const wrist = pose.getLandmark('rightWrist');
 *   const shoulder = pose.getLandmark('rightShoulder');
 *   if (wrist && shoulder && wrist.y < shoulder.y) {
 *     // arm is raised above the shoulder
 *   }
 */
import { BaseTracker } from './BaseTracker.js';
import {
  POSE_LANDMARKS,
  POSE_LANDMARK_COUNT,
  lookupLandmark,
  distance,
  angle,
  midpoint
} from './landmarks.js';

const DEFAULT_CDN =
  'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.18/vision_bundle.mjs';
const DEFAULT_WASM =
  'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.18/wasm';
const DEFAULT_MODEL =
  'https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_lite/float16/1/pose_landmarker_lite.task';

export class PoseTracker extends BaseTracker {
  /**
   * @param {object} [opts]
   * @param {string} [opts.modelUrl]   - override the default Lite model
   * @param {string} [opts.libraryUrl] - override the MediaPipe CDN URL
   * @param {string} [opts.wasmUrl]    - override the WASM bundle URL
   * @param {number} [opts.numPoses=1]
   * @param {number} [opts.minDetectionConfidence=0.5]
   * @param {number} [opts.minTrackingConfidence=0.5]
   */
  constructor(opts = {}) {
    super();
    this.modelUrl = opts.modelUrl || DEFAULT_MODEL;
    this.libraryUrl = opts.libraryUrl || DEFAULT_CDN;
    this.wasmUrl = opts.wasmUrl || DEFAULT_WASM;
    this.numPoses = opts.numPoses || 1;
    this.minDetectionConfidence = opts.minDetectionConfidence == null ? 0.5 : opts.minDetectionConfidence;
    this.minTrackingConfidence = opts.minTrackingConfidence == null ? 0.5 : opts.minTrackingConfidence;

    this._landmarker = null;
    this._video = null;
    this._rafId = null;
  }

  async load() {
    if (this.isAvailable || this.isLoading) return;
    this.isLoading = true;
    try {
      const vision = await import(/* @vite-ignore */ this.libraryUrl);
      const { PoseLandmarker, FilesetResolver } = vision;
      const fileset = await FilesetResolver.forVisionTasks(this.wasmUrl);
      this._landmarker = await PoseLandmarker.createFromOptions(fileset, {
        baseOptions: { modelAssetPath: this.modelUrl, delegate: 'GPU' },
        runningMode: 'VIDEO',
        numPoses: this.numPoses,
        minPoseDetectionConfidence: this.minDetectionConfidence,
        minTrackingConfidence: this.minTrackingConfidence
      });
      this.isAvailable = true;
    } catch (err) {
      this.lastError = err;
      throw err;
    } finally {
      this.isLoading = false;
    }
  }

  async start(videoElement) {
    if (this._destroyed) return;
    if (!this.isAvailable) await this.load();
    this._video = videoElement;
    this._loop();
  }

  stop() {
    if (this._rafId != null && typeof cancelAnimationFrame !== 'undefined') {
      cancelAnimationFrame(this._rafId);
    }
    this._rafId = null;
    if (this._landmarker && typeof this._landmarker.close === 'function') {
      try { this._landmarker.close(); } catch (_) { /* ignore */ }
    }
    this._landmarker = null;
    this.isAvailable = false;
  }

  _loop() {
    if (this._destroyed || !this._video || !this._landmarker) return;
    const now = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    try {
      // The video has to be ready (have dimensions) before we can detect.
      if (this._video.readyState >= 2) {
        const result = this._landmarker.detectForVideo(this._video, now);
        if (result && result.landmarks && result.landmarks.length > 0) {
          this._emit({
            landmarks: result.landmarks[0],
            worldLandmarks: result.worldLandmarks ? result.worldLandmarks[0] : null,
            timestamp: now
          });
        }
      }
    } catch (_) {
      // Ignore per-frame errors so one bad frame doesn't stop the loop.
    }
    if (typeof requestAnimationFrame !== 'undefined') {
      this._rafId = requestAnimationFrame(() => this._loop());
    }
  }

  // ─────────────────────────────────────────────── public landmark accessors

  /**
   * Look up a named pose landmark. Returns `{ x, y, z, visibility }` in
   * normalized [0, 1] image space, or `null` if not detected this frame.
   */
  getLandmark(name) {
    if (!this.latest || !this.latest.landmarks) return null;
    const idx = lookupLandmark(POSE_LANDMARKS, name);
    if (idx < 0 || idx >= POSE_LANDMARK_COUNT) return null;
    return this.latest.landmarks[idx] || null;
  }

  /** Same as getLandmark but for the world-space coordinates (meters). */
  getWorldLandmark(name) {
    if (!this.latest || !this.latest.worldLandmarks) return null;
    const idx = lookupLandmark(POSE_LANDMARKS, name);
    if (idx < 0 || idx >= POSE_LANDMARK_COUNT) return null;
    return this.latest.worldLandmarks[idx] || null;
  }

  /** Joint angle in degrees at point `b`, formed by rays b→a and b→c. */
  jointAngle(a, b, c) {
    return angle(this.getLandmark(a), this.getLandmark(b), this.getLandmark(c));
  }

  /** Pixel-space distance between two named landmarks. */
  distanceBetween(a, b) {
    return distance(this.getLandmark(a), this.getLandmark(b));
  }

  /** Body center: midpoint between the two hip landmarks. */
  getCenter() {
    return midpoint(this.getLandmark('leftHip'), this.getLandmark('rightHip'));
  }

  /** Re-export the names map so games can iterate them. */
  static get LANDMARKS() { return POSE_LANDMARKS; }
}
